import Control from "sap/ui/core/Control";
import Button from "sap/m/Button";
import { AppSettings } from "../constants/AppSettings";
import { MetadataOptions } from "sap/ui/base/ManagedObject";
import Table from "sap/m/Table";
import Toolbar from "sap/m/Toolbar";
import ToolbarSpacer from "sap/m/ToolbarSpacer";
import HBox from "sap/m/HBox";
import { ListBase$UpdateStartedEvent } from "sap/m/ListBase";
import { PaginatingTableBehaviorType } from "../models/PaginatingTableBehaviorType";

/**
 * @namespace akademika.akamont.customControls
 */
export default class PaginatingTable extends Table {
  // 
  // Uses backend pagination. Available behavior modes are: Pagination and InfiniteScroll.
  // Pagination adds a toolbar with pages navigation where it allows the user to select a specific page within the current pages batch e.g. 1-5, 6-10 etc.
  // Infinite scroll operates accurately how the name suggests, default operation can be overriden by setting growingScrollToLoad="false" so user has to click to fetch and show more data.
  // 
  static readonly metadata: MetadataOptions = {
    properties: {
      paginationBehavior: { type: "string" },
      totalItems: { type: "int" }
    },
    events: {
      fetchBatch: {
        parameters: {
          skip: { type: "int" },
          take: { type: "int" },
          behavior: { type: "string" }
        }
      }
    }
  };

  static renderer = { apiVersion: 2 };

  _rowsBatchSize: number;
  _pagesBatchSize = 5;
  _currentPage = 1;
  _currentPagesBatch = 1;
  _totalRows: number;
  private _toolbar: Toolbar | null = null;

  public setPaginationBehavior(newPaginationBehavior: PaginatingTableBehaviorType) {
    this.setProperty("paginationBehavior", newPaginationBehavior, true);
    this._setBehavior();
  }

  public setTotalItems(newTotalItems: number) {
    this.setProperty("totalItems", newTotalItems, true);
    this._totalRows = newTotalItems;
    this._refreshToolbar();
  }

  init() {
    super.init.apply(this);
    this._rowsBatchSize = AppSettings.tableItemsThreshold;

    this.setBusyIndicatorDelay(100);
    this.setAlternateRowColors(true);
  }

  _setBehavior() {
    const paginationBehavior = this.getProperty("paginationBehavior") as PaginatingTableBehaviorType;

    if (paginationBehavior === "Pagination") {
      this.setGrowing(false);
      this.setGrowingScrollToLoad(false);
    } else if (paginationBehavior === "InfiniteScroll") {
      this.setGrowing(true);
      this.setGrowingScrollToLoad(true);
      this.setGrowingThreshold(this._rowsBatchSize - 1);

      this.attachUpdateStarted(this._onInfiniteScroll.bind(this));
    }
  }

  _onInfiniteScroll(oEvent: ListBase$UpdateStartedEvent) {
    if (oEvent.getParameter("reason") === "Growing") {
      const skip = this._currentPage * this._rowsBatchSize;
      const take = this._rowsBatchSize;
      const behavior = this.getProperty("paginationBehavior");

      this._currentPage++;

      this.fireEvent("fetchBatch", { skip, take, behavior });
    }
  }

  _refreshToolbar() {
    if (this._toolbar) {
      this._toolbar.destroy();
      this._toolbar = null;
    }

    if (this.getProperty("paginationBehavior") !== "Pagination") return;

    this._toolbar = new Toolbar({
      content: new HBox({
        items: this._setToolbarPages()
      })
    });

    this.setHeaderToolbar(this._toolbar);
  }

  _setToolbarPages(): Control[] {
    const items: Control[] = [new ToolbarSpacer()];

    const totalPages = Math.ceil(this._totalRows / this._rowsBatchSize);
    const totalBatches = Math.ceil(totalPages / this._pagesBatchSize);

    const buttonsEnabled = {
      previousPagesBatch: this._currentPagesBatch > 1,
      previousPage: this._currentPage > 1,
      nextPagesBatch: this._currentPagesBatch < totalBatches,
      nextPage: this._currentPage < totalPages
    };

    items.push(new Button({
      icon: "sap-icon://close-command-field",
      type: "Default",
      enabled: buttonsEnabled.previousPagesBatch,
      press: () => this._getPreviousPagesBatch()
    }));
    items.push(new Button({
      icon: "sap-icon://navigation-left-arrow",
      type: "Default",
      enabled: buttonsEnabled.previousPage,
      press: () => this._goToPage(this._currentPage - 1)
    }).addStyleClass("sapUiSmallMarginEnd"));

    const startPage = (this._currentPagesBatch - 1) * this._pagesBatchSize + 1;
    const endPage = Math.min(this._pagesBatchSize * this._currentPagesBatch, totalPages);

    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
      const page = pageNumber;
      items.push(new Button({
        text: String(page),
        type: this._currentPage === page ? "Ghost" : "Default",
        press: () => this._goToPage(page)
      }));
    }

    items.push(new Button({
      icon: "sap-icon://navigation-right-arrow",
      type: "Default",
      enabled: buttonsEnabled.nextPage,
      press: () => this._goToPage(this._currentPage + 1)
    }).addStyleClass("sapUiSmallMarginBegin"));
    items.push(new Button({
      icon: "sap-icon://open-command-field",
      type: "Default",
      enabled: buttonsEnabled.nextPagesBatch,
      press: () => this._getNextPagesBatch()
    }));

    return items;
  }

  _goToPage(pageNumber: number) {
    this._currentPage = pageNumber;
    this._currentPagesBatch = Math.ceil(pageNumber / this._pagesBatchSize);

    const skip = (pageNumber - 1) * this._rowsBatchSize;
    const take = this._rowsBatchSize;
    const behavior = this.getProperty("paginationBehavior");

    this.fireEvent("fetchBatch", { skip, take, behavior });
  }

  _getPreviousPagesBatch() {
    if (this._currentPagesBatch === 1) return;
    this._currentPagesBatch--;
    const newPage = (this._currentPagesBatch - 1) * this._pagesBatchSize + 1;
    this._goToPage(newPage);
  }

  _getNextPagesBatch() {
    this._currentPagesBatch++;
    const newPage = (this._currentPagesBatch - 1) * this._pagesBatchSize + 1;
    this._goToPage(newPage);
  }

  public resetPagination() {
    this._currentPage = 1;
    this._currentPagesBatch = 1;
  }
}
